import { gql } from '@apollo/client';
import {
  DAILY_TIPPER_QUESTION_FRAGMENT,
  SUBSCRIPTION_FRAGMENT,
  USER_FRAGMENT,
  USER_QUESTION_FRAGMENT,
  USER_SCORE_FRAGMENT,
} from './fragments';

export const SIGN_IN = gql`
  mutation signIn($email: String, $username: String, $password: String!) {
    userLogin(email: $email, username: $username, password: $password) {
      token
      user {
        ...user_fragment
      }
    }
  }
`;

export const SIGN_UP = gql`
  mutation signUp(
    $firstName: String
    $lastName: String
    $email: String
    $username: String
    $dateOfBirth: Date
    $gender: Int
    $postcode: String
    $specialityId: UUID
    $anotherWorkplace: String
    $anotherProfession: String
    $efnNumber: String
    $examYear: Int
    $subscribeToNews: Boolean
    $password: String
    $passwordConfirmation: String
    $studyBeginId: UUID
    $educationLevelId: UUID
    $universityId: UUID
    $semesterId: UUID
    $sharingSenderId: UUID
    $registrationNumber: String
    $medicineStudyBegin: Date
    $workplaceId: UUID
    $professionId: UUID
    $studyYearId: UUID
    $deepLinkHash: String
    $googleAdsMetadata: GenericScalar
  ) {
    userRegistration(
      firstName: $firstName
      lastName: $lastName
      email: $email
      username: $username
      dateOfBirth: $dateOfBirth
      gender: $gender
      postcode: $postcode
      specialityId: $specialityId
      anotherWorkplace: $anotherWorkplace
      anotherProfession: $anotherProfession
      efnNumber: $efnNumber
      examYear: $examYear
      subscribeToNews: $subscribeToNews
      password: $password
      passwordConfirmation: $passwordConfirmation
      studyBeginId: $studyBeginId
      educationLevelId: $educationLevelId
      universityId: $universityId
      semesterId: $semesterId
      sharingSenderId: $sharingSenderId
      registrationNumber: $registrationNumber
      medicineStudyBegin: $medicineStudyBegin
      workplaceId: $workplaceId
      professionId: $professionId
      studyYearId: $studyYearId
      deepLinkHash: $deepLinkHash
      googleAdsMetadata: $googleAdsMetadata
    ) {
      token
      user {
        ...user_fragment
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const UPDATE_USER_PROFILE = gql`
  mutation updateUserProfile(
    $anotherWorkplace: String
    $anotherProfession: String
    $dateOfBirth: Date
    $educationLevelId: UUID
    $examYear: Int
    $firstName: String
    $gender: Int
    $lastName: String
    $medicineStudyBegin: Date
    $postcode: String
    $registrationNumber: String
    $efnNumber: String
    $semesterId: UUID
    $specialityId: UUID
    $studyBeginId: UUID
    $studyYearId: UUID
    $universityId: UUID
    $username: String
    $workplaceId: UUID
  ) {
    updateUserData(
      anotherWorkplace: $anotherWorkplace
      anotherProfession: $anotherProfession
      dateOfBirth: $dateOfBirth
      educationLevelId: $educationLevelId
      examYear: $examYear
      firstName: $firstName
      gender: $gender
      lastName: $lastName
      medicineStudyBegin: $medicineStudyBegin
      postcode: $postcode
      registrationNumber: $registrationNumber
      efnNumber: $efnNumber
      semesterId: $semesterId
      specialityId: $specialityId
      studyBeginId: $studyBeginId
      studyYearId: $studyYearId
      universityId: $universityId
      username: $username
      workplaceId: $workplaceId
    ) {
      token
      user {
        ...user_fragment
      }
    }
  }
  ${USER_QUESTION_FRAGMENT}
`;

export const RESET_PASSWORD = gql`
  mutation resetPassword(
    $newPassword: String
    $newPasswordConfirmation: String
    $resetPasswordKey: String
  ) {
    userRestorePassword(
      newPassword: $newPassword
      newPasswordConfirmation: $newPasswordConfirmation
      resetPasswordKey: $resetPasswordKey
    ) {
      user {
        id
      }
    }
  }
`;

export const CONFIRM_REGISTRATION = gql`
  mutation confirmToken($token: UUID) {
    userConfirm(token: $token) {
      user {
        id
        email
      }
    }
  }
`;

export const UPLOAD_PHOTO = gql`
  mutation uploadPhoto($file: Upload!) {
    uploadPhoto(file: $file) {
      user {
        id
        avatar
      }
    }
  }
`;

export const APPLY_NETWORK_CODE = gql`
  mutation applyNetworkCode($code: Int) {
    applyNetworkCodeForUser(code: $code) {
      codes {
        code
      }
    }
  }
`;

export const APPLY_CLASSROOM_CODE = gql`
  mutation applyClassroomCode(
    $code: Int
    $sessionId: UUID
    $isNetworkType: Boolean
    $isAvailableTab: Boolean
    $specialityGroupIcon: String
    $deepLinkHash: String
  ) {
    applyClassRoomCode(
      code: $code
      sessionId: $sessionId
      isNetworkType: $isNetworkType
      isAvailableTab: $isAvailableTab
      specialityGroupIcon: $specialityGroupIcon
      deepLinkHash: $deepLinkHash
    ) {
      classRoom {
        id
        classRoom {
          id
          enrollmentType
        }
      }
    }
  }
`;

export const JOIN_NETWORK_CLASSROOM = gql`
  mutation joinNetworkClassRoom(
    $sessionId: UUID
    $deepLinkHash: String
    $specialityGroupIcon: String
    $isAfterOnboarding: Boolean
  ) {
    joinToNetworkClassRoom(
      sessionId: $sessionId
      deepLinkHash: $deepLinkHash
      specialityGroupIcon: $specialityGroupIcon
      isAfterOnboarding: $isAfterOnboarding
    ) {
      classRoomSession {
        id
        classRoom {
          id
          enrollmentType
        }
      }
    }
  }
`;

export const JOIN_BLENDED_CLASSROOM = gql`
  mutation joinBlendedClassRoom($sessionId: UUID, $deepLinkHash: String) {
    joinToBlendedClassRoom(sessionId: $sessionId, deepLinkHash: $deepLinkHash) {
      classRoomSession {
        id
        classRoom {
          id
          enrollmentType
        }
      }
    }
  }
`;

export const JOIN_LEK_CLASSROOM = gql`
  mutation joinLekClassRoom($sessionId: UUID, $deepLinkHash: String) {
    joinToLekClassRoom(sessionId: $sessionId, deepLinkHash: $deepLinkHash) {
      classRoomSession {
        id
        classRoom {
          id
          enrollmentType
        }
      }
    }
  }
`;

export const JOIN_CME_CLASSROOM = gql`
  mutation joinCmeClassRoom(
    $deepLinkHash: String
    $isAfterOnboarding: Boolean
    $sessionId: UUID
    $specialityGroupIcon: String
  ) {
    joinToCmeClassRoom(
      deepLinkHash: $deepLinkHash
      isAfterOnboarding: $isAfterOnboarding
      sessionId: $sessionId
      specialityGroupIcon: $specialityGroupIcon
    ) {
      classRoomSession {
        id
        classRoom {
          id
          enrollmentType
        }
      }
    }
  }
`;

export const CREATE_USER_DUEL = gql`
  mutation createUserDuel($opponentId: UUID, $sessionId: UUID, $isCustomDuel: Boolean) {
    createDuel(opponentId: $opponentId, sessionId: $sessionId, isCustomDuel: $isCustomDuel) {
      duel {
        id
        status
        duelRound {
          id
          roundNumber
          userQuestions {
            ...user_question_fragment
          }
        }
      }
      errors
    }
  }
  ${USER_QUESTION_FRAGMENT}
`;

export const CREATE_USER_ROUND = gql`
  mutation createUserRound($duelId: UUID) {
    createRound(duelId: $duelId) {
      round {
        id
        roundNumber
        # Update Cache
        duel {
          id
          status
          duelRound {
            id
            roundNumber
            userQuestions {
              ...user_question_fragment
            }
          }
        }
        userQuestions {
          ...user_question_fragment
        }
      }
    }
  }
  ${USER_FRAGMENT}
`;

export const SET_USER_ANSWER = gql`
  mutation setUserAnswer($duelRoundQuestionAnswerId: UUID, $answerId: UUID) {
    setAnswer(duelRoundQuestionAnswerId: $duelRoundQuestionAnswerId, answerId: $answerId) {
      duelRoundQuestionAnswer {
        duelRound {
          id
          duel {
            id
            status
            result {
              currentUserData {
                ...user_score_fragment
              }
              opponentData {
                ...user_score_fragment
              }
            }
          }
          userQuestions {
            ...user_question_fragment
          }
        }
      }
    }
  }
  ${USER_SCORE_FRAGMENT}
  ${USER_QUESTION_FRAGMENT}
`;

export const SET_DAILY_TIPPER_ANSWER = gql`
  mutation setUserDailyTipperAnswer($questionId: UUID, $answerId: UUID) {
    setDailyTipperAnswer(questionId: $questionId, answerId: $answerId) {
      dailyTipperQuestionAnswerStatus {
        dailyTipperQuestionAnswer {
          ...daily_tipper_question_fragment
        }
        dailyStatus
      }
    }
  }
  ${DAILY_TIPPER_QUESTION_FRAGMENT}
`;

export const SET_ONBOARDING_ANSWER = gql`
  mutation setOnboardingAnswer($questionId: UUID, $answerId: UUID) {
    setOnboardingAnswer(questionId: $questionId, answerId: $answerId) {
      userOnboardingQuestionAnswerData {
        answerIsCorrect
        question {
          id
        }
        userAnswer {
          id
          value
        }
        correctAnswer {
          id
          value
        }
      }
    }
  }
`;

export const NOTIFICATION_MUTATION = gql`
  mutation readNotification($id: UUID) {
    notificationStatus(notificationId: $id) {
      notification {
        id
        isRead
      }
    }
  }
`;

export const UNSUBSCRIBE_TO_NEWS = gql`
  mutation unsubscribeToNews($userId: UUID) {
    userUnsubscribeToNews(userId: $userId) {
      user {
        id
      }
    }
  }
`;

export const SUBSCRIBE_TO_NEWS = gql`
  mutation subscribeToNews($userId: UUID) {
    userSubscribeToNews(userId: $userId) {
      user {
        id
      }
    }
  }
`;

export const UPDATE_DT_SETTINGS = gql`
  mutation updateDTSettings($goal: Int, $mode: Boolean) {
    updateUserSettings(goal: $goal, dailyTipperMode: $mode) {
      userSettings {
        id
        goal
        dailyTipperMode
      }
    }
  }
`;

export const UPDATE_DUELS_SETTINGS = gql`
  mutation updateDuelsSettings($mode: Boolean) {
    updateUserSettings(duelMode: $mode) {
      userSettings {
        id
        duelMode
      }
    }
  }
`;

export const UPDATE_PUSH_NOTIFICATIONS_SETTINGS = gql`
  mutation updatePushNotificationsSettings(
    $pushNotificationMode: Boolean
    $pushNotificationDailyTipperMode: Boolean
    $pushNotificationDuelMode: Boolean
    $pushNotificationNewChapterMode: Boolean
    $pushNotificationPremiumMode: Boolean
    $pushNotificationQuizTimeMode: Boolean
  ) {
    updateUserSettings(
      pushNotificationMode: $pushNotificationMode
      pushNotificationDailyTipperMode: $pushNotificationDailyTipperMode
      pushNotificationDuelMode: $pushNotificationDuelMode
      pushNotificationNewChapterMode: $pushNotificationNewChapterMode
      pushNotificationPremiumMode: $pushNotificationPremiumMode
      pushNotificationQuizTimeMode: $pushNotificationQuizTimeMode
    ) {
      userSettings {
        id
        pushNotificationMode
        pushNotificationDailyTipperMode
        pushNotificationDuelMode
        pushNotificationNewChapterMode
        pushNotificationPremiumMode
        pushNotificationQuizTimeMode
      }
    }
  }
`;

export const UPDATE_RULES_SETTINGS = gql`
  mutation updateRulesSettings(
    $isDailyTipperRulesRead: Boolean
    $isDuelRulesRead: Boolean
    $isQuizDuelRulesRead: Boolean
    $isIntroCompleted: Boolean
  ) {
    updateUserSettings(
      isDailyTipperRulesRead: $isDailyTipperRulesRead
      isDuelRulesRead: $isDuelRulesRead
      isQuizDuelRulesRead: $isQuizDuelRulesRead
      isIntroCompleted: $isIntroCompleted
    ) {
      userSettings {
        id
        isDailyTipperRulesRead
        isDuelRulesRead
        isQuizDuelRulesRead
        isIntroCompleted
      }
    }
  }
`;

export const UPDATE_CLASSROOM_SETTINGS = gql`
  mutation updateClassroomSettings(
    $sessionId: UUID
    $dailyTipperMode: Boolean
    $duelMode: Boolean
    $isAchieved: Boolean
    $isClosed: Boolean
    $isOut: Boolean
  ) {
    updateUserClassRoomSettings(
      sessionId: $sessionId
      dailyTipperMode: $dailyTipperMode
      duelMode: $duelMode
      isAchieved: $isAchieved
      isClosed: $isClosed
      isOut: $isOut
    ) {
      userClassRoomSettings {
        id
        isDuelModeEnabled
        isDailyTipperModeEnabled
        isAchieved
        isClosed
        isOut
      }
    }
  }
`;

export const UPDATE_IS_INTRO_COMPLETED_SETTINGS = gql`
  mutation UpdateIsIntroCompletedSettings($isIntroCompleted: Boolean) {
    updateUserSettings(isIntroCompleted: $isIntroCompleted) {
      userSettings {
        id
        isIntroCompleted
      }
    }
  }
`;

export const UPDATE_IS_CLASSROOM_INTRO_COMPLETED_SETTINGS = gql`
  mutation UpdateIsClassRoomIntroCompletedSettings($isClassRoomIntroCompleted: Boolean) {
    updateUserSettings(isClassRoomIntroCompleted: $isClassRoomIntroCompleted) {
      userSettings {
        id
        isClassRoomIntroCompleted
      }
    }
  }
`;

export const UPDATE_COOKIE_ACCEPTED_SETTINGS = gql`
  mutation updateCookieAcceptedSettings(
    $isCookieAccepted: Boolean
    $isMarketingCookieAccepted: Boolean
  ) {
    updateUserSettings(
      isCookieAccepted: $isCookieAccepted
      isMarketingCookieAccepted: $isMarketingCookieAccepted
    ) {
      userSettings {
        id
        isCookieAccepted
        isMarketingCookieAccepted
      }
    }
  }
`;

export const LEAVE_CLASSROOM = gql`
  mutation leaveClassroom($sessionId: UUID) {
    leaveClassRoom(sessionId: $sessionId) {
      learningSessionSettings {
        id
      }
    }
  }
`;

export const LEAVE_NETWORK_CLASSROOM = gql`
  mutation leaveNetworkClassroom($userSessionSettingsId: UUID) {
    leaveNetworkClassRoom(userSessionSettingsId: $userSessionSettingsId) {
      learningSessionSettings {
        id
      }
    }
  }
`;

export const EARLY_FINISH_NETWORK_CLASSROOM = gql`
  mutation earlyFinishNetworkClassroom($userSessionSettingsId: UUID) {
    earlyFinishNetworkClassRoom(userSessionSettingsId: $userSessionSettingsId) {
      learningSessionSettings {
        id
      }
    }
  }
`;

export const EARLY_FINISH_BLENDED_CLASSROOM = gql`
  mutation earlyFinishBlendedClassroom($userSessionSettingsId: UUID) {
    earlyFinishBlendedClassRoom(userSessionSettingsId: $userSessionSettingsId) {
      learningSessionSettings {
        id
      }
    }
  }
`;

export const EARLY_FINISH_LEK_CLASSROOM = gql`
  mutation earlyFinishLekClassroom($userSessionSettingsId: UUID) {
    earlyFinishLekClassRoom(userSessionSettingsId: $userSessionSettingsId) {
      learningSessionSettings {
        id
      }
    }
  }
`;

export const START_BLENDED_CLASSROOM = gql`
  mutation resumeBlendedClassroom($userSessionSettingsId: UUID!) {
    resumeBlendedClassRoom(userSessionSettingsId: $userSessionSettingsId) {
      learningSessionSettings {
        id
        classRoomLearningSession {
          id
        }
      }
    }
  }
`;

export const PAUSE_BLENDED_CLASSROOM = gql`
  mutation pauseBlendedClassroom($userSessionSettingsId: UUID!) {
    pauseBlendedClassRoom(userSessionSettingsId: $userSessionSettingsId) {
      learningSessionSettings {
        id
        classRoomLearningSession {
          id
        }
      }
    }
  }
`;

export const USER_LEK_START = gql`
  mutation userLekStart($lekSessionId: UUID, $startTime: DateTime) {
    lekStart(lekSessionId: $lekSessionId, startTime: $startTime) {
      userLekData {
        id
        created
        status
        elapsedTime
        lekSession {
          id
        }
        userLekQuestionAnswerData {
          id
          question {
            id
          }
          userAnswer {
            id
          }
        }
      }
    }
  }
`;

export const UPDATE_CHROME_INSTALLATION_POPUP_SKIPPED_SETTINGS = gql`
  mutation updateIsChromeInstallationPopupSkippedSettings(
    $isChromeInstallationPopupSkipped: Boolean
  ) {
    updateUserSettings(isChromeInstallationPopupSkipped: $isChromeInstallationPopupSkipped) {
      userSettings {
        id
        isChromeInstallationPopupSkipped
      }
    }
  }
`;

export const USER_LEK_FINISH = gql`
  mutation userLekFinish($lekSessionId: UUID, $userLekDataId: UUID) {
    lekFinish(lekSessionId: $lekSessionId, userLekDataId: $userLekDataId) {
      userLekData {
        id
        status
        elapsedTime
        isPassed
        correctAnswerCount
      }
    }
  }
`;

export const SET_USER_LEK_ANSWER = gql`
  mutation setUserLekAnswer(
    $answerId: UUID
    $lekSessionId: UUID
    $questionId: UUID
    $answerTime: DateTime
  ) {
    setLekAnswer(
      answerId: $answerId
      lekSessionId: $lekSessionId
      questionId: $questionId
      answerTime: $answerTime
    ) {
      userLekQuestionAnswerData {
        id
        userLekData {
          id
          status
        }
      }
    }
  }
`;

export const DELETE_ACCOUNT = gql`
  mutation deleteUserAccount {
    deleteAccount {
      isDeleted
    }
  }
`;

export const CREATE_STRIPE_CHECKOUT_SESSION = gql`
  mutation createStripeCheckoutSession($mode: String, $returnUrl: String, $stripePriceId: String!) {
    createCheckoutSession(mode: $mode, returnUrl: $returnUrl, stripePriceId: $stripePriceId) {
      session
    }
  }
`;

export const CHANGE_STRIPE_SUBSCRIPTION = gql`
  mutation changeStripeSubscription($stripePriceId: String) {
    changeSubscription(stripePriceId: $stripePriceId) {
      subscription {
        id
        status
        currentPeriodEnd
        requestCancel
        upgradeStatus
        product {
          id
          feId
          name
        }
        price {
          id
          type
          amount
          interval
          intervalCount
          trialPeriodDays
          stripeProductId
          appleStoreId
          googlePlayId
        }
      }
    }
  }
`;

export const DELETE_STRIPE_PAYMENT_METHOD = gql`
  mutation deleteStripePaymentMethod {
    deleteDefaultPaymentMethod {
      customer {
        id
        defaultCard {
          brand
          last4
        }
      }
    }
  }
`;

export const CREATE_SETUP_CHECKOUT_SESSION = gql`
  mutation createStripeSetupCheckoutSession($returnUrl: String, $stripePriceId: String!) {
    createSetupCheckoutSession(returnUrl: $returnUrl, stripePriceId: $stripePriceId) {
      session
    }
  }
`;

export const CANCEL_USER_SUBSCRIPTION = gql`
  mutation cancelUserSubscription {
    cancelSubscription {
      subscription {
        id
        status
        currentPeriodEnd
        requestCancel
        product {
          id
          feId
          name
        }
        price {
          id
          type
          amount
          interval
          intervalCount
          trialPeriodDays
          stripeProductId
          appleStoreId
          googlePlayId
        }
      }
    }
  }
`;

export const RENEW_PLAN = gql`
  mutation renewPlan {
    renewSubscription {
      subscription {
        id
        status
        currentPeriodEnd
        requestCancel
        product {
          id
          feId
          name
        }
        price {
          id
          type
          amount
          interval
          intervalCount
          trialPeriodDays
          stripeProductId
          appleStoreId
          googlePlayId
        }
      }
    }
  }
`;

export const IN_APP_SUBSCRIBE = gql`
  mutation inAppUserSubscribe($subscriptionObject: GenericScalar) {
    inAppSubscribe(subscriptionObject: $subscriptionObject) {
      inAppSubscription {
        id
        me {
          id
          isPremium
          isInAppPremium
          isPromotionPremium
        }
        subscription {
          ...subscription_fragment
        }
      }
    }
  }
  ${SUBSCRIPTION_FRAGMENT}
`;

export const UPGRADE_IN_APP_SUBSCRIBTION = gql`
  mutation upgradeInAppSubscription($subscriptionObject: GenericScalar) {
    changeInAppSubscription(subscriptionObject: $subscriptionObject) {
      inAppSubscription {
        id
        me {
          id
          isPremium
          isInAppPremium
          isPromotionPremium
        }
        subscription {
          ...subscription_fragment
        }
      }
    }
  }
  ${SUBSCRIPTION_FRAGMENT}
`;

export const UPDATE_IOS_INSTALLATION_POPUP_SKIPPED_SETTINGS = gql`
  mutation updateIsIosInstallationPopupSkippedSettings($isIosInstallationPopupSkipped: Boolean) {
    updateUserSettings(isIosInstallationPopupSkipped: $isIosInstallationPopupSkipped) {
      userSettings {
        id
        isIosInstallationPopupSkipped
      }
    }
  }
`;

export const APPLY_QUIZTIME_CODE = gql`
  mutation applyQuizTimeCode($code: Int, $sessionId: UUID!, $deepLinkHash: String) {
    joinToQuiz(code: $code, sessionId: $sessionId, deepLinkHash: $deepLinkHash) {
      quizSession {
        id
        quizTime {
          id
        }
      }
    }
  }
`;

export const LEAVE_QUIZ_TIME = gql`
  mutation LeaveQuizTime($sessionId: UUID!) {
    leaveQuiz(sessionId: $sessionId) {
      session {
        id
      }
    }
  }
`;

export const START_QUIZ = gql`
  mutation startQuiz($quizSessionId: UUID!) {
    quizStartByUser(quizSessionId: $quizSessionId) {
      quizSession {
        id
        status
      }
    }
  }
`;

export const PAUSE_QUIZ = gql`
  mutation pauseQuiz($pauseMode: Boolean!, $quizSessionId: UUID!) {
    switchPauseMode(pauseMode: $pauseMode, quizSessionId: $quizSessionId) {
      userQuizSessionSettings {
        id
        userStatus
      }
    }
  }
`;

export const CREATE_QUIZTIME_DUEL = gql`
  mutation createQuiztimeDuel($quizSessionId: UUID!) {
    createQuizDuel(quizSessionId: $quizSessionId) {
      duel {
        id
        status
        duelRound {
          id
          roundNumber
          userQuestions {
            ...user_question_fragment
          }
        }
      }
    }
  }
  ${USER_QUESTION_FRAGMENT}
`;

export const CREATE_QUIZTIME_DUEL_BY_OPPONENT = gql`
  mutation createQuiztimeDuelByOpponent($opponentId: UUID!, $quizSessionId: UUID!) {
    createQuizDuelByOpponent(opponentId: $opponentId, quizSessionId: $quizSessionId) {
      duel {
        id
        status
        duelRound {
          id
          roundNumber
          userQuestions {
            ...user_question_fragment
          }
        }
      }
    }
  }
  ${USER_QUESTION_FRAGMENT}
`;

export const ADD_WAITING_USER_NOTIFICATION = gql`
  mutation addWaitingUserNotification($sessionId: UUID!) {
    addWaitingUser(sessionId: $sessionId) {
      waitingUser {
        id
        quizSession {
          id
          usersCount
        }
      }
    }
  }
`;

export const GET_DEEP_LINK = gql`
  mutation getDeepLink($hash: String!, $source: String) {
    getDeepLink(hash: $hash, source: $source) {
      deepLink {
        targetObjectId
        targetType
      }
    }
  }
`;

export const CREATE_FEEDBACK_QUESTION = gql`
  mutation createFeedbackQuestion(
    $questionId: UUID!
    $interestRate: Int!
    $relevanceRate: Int!
    $comment: String
  ) {
    createFeedbackForQuestion(
      questionId: $questionId
      interestRate: $interestRate
      relevanceRate: $relevanceRate
      comment: $comment
    ) {
      feedbackForQuestion {
        id
        relevanceRate
        interestRate
        comment
        question {
          id
          value
        }
      }
    }
  }
`;

export const EDIT_FEEDBACK_QUESTION = gql`
  mutation editFeedbackQuestion(
    $questionId: UUID!
    $interestRate: Int
    $relevanceRate: Int
    $comment: String
  ) {
    editFeedbackForQuestion(
      questionId: $questionId
      interestRate: $interestRate
      relevanceRate: $relevanceRate
      comment: $comment
    ) {
      feedbackForQuestion {
        id
        relevanceRate
        interestRate
        comment
        question {
          id
          value
        }
      }
    }
  }
`;

export const SET_EXPO_TOKEN = gql`
  mutation setExpoToken($expoToken: String) {
    setUserExpoToken(expoToken: $expoToken) {
      result
    }
  }
`;
